import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import SEO from '../components/SEO';

const values = [
  {
    icon: '🧒',
    title: 'Children First',
    desc: 'Every feature decision starts with one question: is this genuinely good for a child? We never show advertising. We never profile children. We never monetise childhood.',
  },
  {
    icon: '🔒',
    title: 'Privacy as a Default',
    desc: 'We collect the minimum data needed to make the magic work. Your child\'s photo and voice are yours. We will never use them for AI training, sell them, or share them beyond what\'s necessary to generate the story you asked for.',
  },
  {
    icon: '📚',
    title: 'Stories That Actually Matter',
    desc: 'Generic bedtime stories are everywhere. We believe the ones that stick are the ones where your child is the hero — where their name, their interests, their world become the story. That\'s what we\'re building toward.',
  },
  {
    icon: '🌍',
    title: 'Every Family, Every Language',
    desc: 'A child in Mumbai should hear a bedtime story in Hindi. A family in Seville should read one in Spanish. We support 11 languages and counting, because every family deserves stories in the language of their home.',
  },
  {
    icon: '🎙️',
    title: 'The Sound of Someone Who Loves You',
    desc: 'Reading aloud to your children is one of the most powerful things you can do for them. Voice cloning lets that continue even when you\'re travelling, working nights, or simply too tired. It\'s not a replacement for you — it\'s you, when you can\'t be there.',
  },
  {
    icon: '🛡️',
    title: 'Safe Content, Always',
    desc: 'Every story goes through automated content moderation. Parents can enable an Approval Queue to review stories before children see them. We would rather be over-cautious than let a single inappropriate story reach a child.',
  },
];

const timeline = [
  {
    year: 'Jan 2026',
    title: 'The idea & first version',
    desc: 'A late night, a tired toddler, and the same three picture books read for the hundredth time. The question: what if tonight\'s story was different — and always about them? A few weeks later, the first version was live: enter a name, age, and a favourite animal, and get a unique story in seconds.',
  },
  {
    year: 'Mar 2026',
    title: 'Illustrations, narration & 11 languages',
    desc: 'We added watercolour illustrations to every story, AI narration so stories could be heard not just read, and launched in 11 languages so families everywhere could participate.',
  },
  {
    year: 'May 2026',
    title: 'Voice cloning & photo illustrations',
    desc: 'The two most requested features: stories narrated in a parent\'s own voice, and illustrations where the child is literally the hero. Both launched on the Family plan.',
  },
  {
    year: 'Now',
    title: 'Growing with families',
    desc: 'Story Series for multi-chapter adventures, reading reports, bedtime reminders, grandparent sharing, community stories, and gift stories — building toward the complete bedtime companion.',
  },
];

const stats = [
  { value: '11', label: 'Languages supported' },
  { value: '15s', label: 'Average generation time' },
  { value: '5 plans', label: 'Voice cloning, photos, series, quizzes & more' },
  { value: '0', label: 'Ads. Ever.' },
];

const AboutPage = () => {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <SEO
        title="About Us — Dreamy Bedtime"
        description="Learn the story behind Dreamy Bedtime — why we built it, what we believe, and how we're working to make bedtime magical for every family."
        canonical="/about"
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-dream text-white" style={{ padding: '72px 0 56px' }}>
        <div className="container text-center" style={{ maxWidth: '680px' }}>
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill">🌙 Our Story</span>
          <h1 className="fw-bold mb-4" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(32px,5vw,52px)', lineHeight: 1.2 }}>
            We believe every child deserves a story written just for them
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '18px', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto' }}>
            Dreamy Bedtime started as a question asked at 10pm by an exhausted parent: what if tonight's story was different — and what if the hero had my daughter's name?
          </p>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ background: 'var(--card-bg)', borderBottom: '1px solid #f0eaf8', padding: '28px 0' }}>
        <div className="container">
          <div className="row g-3 text-center">
            {stats.map(s => (
              <div key={s.label} className="col-6 col-md-3">
                <div className="fw-bold" style={{ fontSize: '28px', color: '#6f42c1', fontFamily: "'Fredoka One',cursive" }}>{s.value}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-soft)', lineHeight: 1.4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container py-5" style={{ maxWidth: '860px' }}>

        {/* Mission */}
        <div className="card border-0 shadow-sm p-4 p-lg-5 mb-5" style={{ borderRadius: '24px' }}>
          <h2 className="fw-bold mb-4" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '26px' }}>
            Why we built this
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '16px', lineHeight: 1.9 }}>
            Bedtime stories are one of the most important things a parent can give a child. Not because of the words — because of the ritual. The pause from screens, the closeness, the specific moment when a child hears their name in a story and their eyes go wide.
          </p>
          <p className="text-muted mb-4" style={{ fontSize: '16px', lineHeight: 1.9 }}>
            The problem is that bedtime comes every night, and inspiration doesn't always. The same books get read until the covers fall off. Tired parents skip pages. The magic fades into routine.
          </p>
          <p className="text-muted mb-4" style={{ fontSize: '16px', lineHeight: 1.9 }}>
            We built Dreamy Bedtime to solve that — to make every night's story feel like it was written specifically for your child, because it was. Not a template with a name swapped in. A real story built around who they actually are: their age, their obsessions, the characters they love, the language they dream in.
          </p>
          <p className="text-muted" style={{ fontSize: '16px', lineHeight: 1.9 }}>
            We are a small team that cares deeply about doing this right. That means privacy-first, child-safe content, no advertising, and a product that gets better every week because real families tell us what they need.
          </p>
        </div>

        {/* Timeline */}
        <div className="card border-0 shadow-sm p-4 p-lg-5 mb-5" style={{ borderRadius: '24px' }}>
          <h2 className="fw-bold mb-4" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '26px' }}>
            How we got here
          </h2>
          <div className="position-relative" style={{ paddingLeft: '28px', borderLeft: '3px solid #e8d8ff' }}>
            {timeline.map((item, i) => (
              <div key={i} className="position-relative mb-5">
                <div
                  className="position-absolute"
                  style={{
                    left: '-38px',
                    top: '2px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: '#6f42c1',
                    border: '3px solid #fff',
                    boxShadow: '0 0 0 3px #e8d8ff',
                  }}
                />
                <span className="badge rounded-pill mb-2" style={{ background: 'rgba(111,66,193,0.1)', color: '#6f42c1', fontSize: '12px', fontWeight: 700, padding: '4px 12px' }}>
                  {item.year}
                </span>
                <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '16px' }}>{item.title}</h5>
                <p className="text-muted mb-0" style={{ fontSize: '15px', lineHeight: 1.7 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="card border-0 shadow-sm p-4 p-lg-5 mb-5" style={{ borderRadius: '24px' }}>
          <h2 className="fw-bold mb-4" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '26px' }}>
            What we believe
          </h2>
          <div className="row g-4">
            {values.map(v => (
              <div key={v.title} className="col-md-6">
                <div className="d-flex gap-3">
                  <span style={{ fontSize: '28px', lineHeight: 1, flexShrink: 0 }}>{v.icon}</span>
                  <div>
                    <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{v.title}</h6>
                    <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.7 }}>{v.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How the AI works */}
        <div className="card border-0 shadow-sm p-4 p-lg-5 mb-5" style={{ borderRadius: '24px' }}>
          <h2 className="fw-bold mb-4" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '26px' }}>
            How the AI works
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8 }}>
            We use large language models (GPT-4o via OpenAI's API) to generate story text, and specialised image generation models to create the watercolour illustrations. Narration is powered by text-to-speech technology, and voice cloning is handled by Cartesia's voice infrastructure.
          </p>
          <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8 }}>
            Every story prompt is carefully engineered to produce age-appropriate, educationally sound narratives that feel genuinely creative — not templated. We pass your child's name, age, interests, and selected theme into the prompt, and the AI writes an original story around them. We then run automated content moderation on every output before it reaches your library.
          </p>
          <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
            <strong>Important:</strong> We do not use your data to train AI models. We use OpenAI's API on an enterprise plan under a data processing agreement that explicitly prohibits use of API inputs for model training. The same applies to our other AI providers.
          </p>
          <div className="rounded-3 p-3" style={{ background: 'rgba(111,66,193,0.06)', border: '1px solid rgba(111,66,193,0.15)' }}>
            <p className="mb-0" style={{ fontSize: '14px', color: '#6f42c1', fontWeight: 600 }}>
              🔒 Your child's name, photo, and voice are never used to train AI. They are used only to generate the content you explicitly request, and for nothing else.
            </p>
          </div>
        </div>

        {/* Company */}
        <div className="card border-0 shadow-sm p-4 p-lg-5 mb-5" style={{ borderRadius: '24px' }}>
          <h2 className="fw-bold mb-4" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '26px' }}>
            The company
          </h2>
          <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
            Dreamy Bedtime is built and operated by <strong>Prajapati LLC</strong>. We are a small, independent company — not a venture-backed startup chasing growth metrics. We charge a fair subscription price because that's how we keep the lights on and keep improving the product without resorting to advertising or selling data.
          </p>
          <p className="text-muted" style={{ fontSize: '15px', lineHeight: 1.8 }}>
            For questions, feedback, or just to say hello — we read every email. Reach us at{' '}
            <a href="mailto:support@dreamybedtime.com" style={{ color: '#6f42c1', fontWeight: 600 }}>support@dreamybedtime.com</a>.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center p-5 rounded-4" style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
          <h3 className="fw-bold text-white mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: '28px' }}>
            Ready to start the magic?
          </h3>
          <p style={{ color: '#c9a8ff', marginBottom: '24px', fontSize: '16px' }}>
            Your first 5 stories are free. No credit card required.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link to="/register" className="btn btn-warning fw-bold rounded-pill px-5 py-2" style={{ fontSize: '16px' }}>
              Get Started Free
            </Link>
            <Link to="/pricing" className="btn btn-outline-light fw-semibold rounded-pill px-4 py-2" style={{ fontSize: '16px' }}>
              See Plans
            </Link>
          </div>
        </div>

      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #f0eaf8', padding: '28px 0 32px', background: 'var(--card-bg)' }}>
        <div className="container text-center">
          <p className="text-muted small mb-2">
            <Link to="/faq" style={{ color: '#6f42c1' }}>FAQ</Link>
            {' · '}
            <Link to="/blog" style={{ color: '#6f42c1' }}>Blog</Link>
            {' · '}
            <Link to="/contact" style={{ color: '#6f42c1' }}>Contact</Link>
            {' · '}
            <Link to="/privacy" style={{ color: '#6f42c1' }}>Privacy Policy</Link>
            {' · '}
            <Link to="/terms" style={{ color: '#6f42c1' }}>Terms of Service</Link>
            {' · '}
            <Link to="/cookies" style={{ color: '#6f42c1' }}>Cookie Policy</Link>
          </p>
          <p className="text-muted" style={{ fontSize: '12px', margin: 0 }}>© 2026 Prajapati LLC · All rights reserved</p>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
